import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Guest session (EduPro onboarding without account)
const GUEST_KEY = 'edupro_guest_id';
export const ensureGuestId = () => {
  let g = localStorage.getItem(GUEST_KEY);
  if (!g) {
    g = 'g' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(GUEST_KEY, g);
  }
  return g;
};
export const getGuestId = () => localStorage.getItem(GUEST_KEY);
export const clearGuestId = () => localStorage.removeItem(GUEST_KEY);

// Attach the guest id (if any) so the backend can resolve unauthenticated actors
api.interceptors.request.use((config) => {
  const g = localStorage.getItem(GUEST_KEY);
  if (g) config.headers['X-Guest-Id'] = g;
  return config;
});

// Universities
export const getUniversities = () => api.get('/universities');
export const getUniversity = (id) => api.get(`/universities/${id}`);

// Programs
export const getPrograms = (params) => api.get('/programs', { params });
export const getProgram = (id) => api.get(`/programs/${id}`);
export const getCategories = () => api.get('/programs/categories/list');

// Calculator
export const calculateLoan = (data) => api.post('/calculator/loan', data);
export const calculateProgramLoan = (data) => api.post('/calculator/program', data);
export const calculateAmortization = (data) => api.post('/calculator/amortization', data);

// Applications
export const createApplication = (data) => api.post('/applications', data);
export const getApplications = () => api.get('/applications');
export const getApplication = (id) => api.get(`/applications/${id}`);
export const updateApplication = (id, data) => api.patch(`/applications/${id}`, data);
export const submitApplication = (id) => api.post(`/applications/${id}/submit`);
export const getApplicationTimeline = (id) => api.get(`/applications/${id}/timeline`);

// Contracts
export const signContract = (data) => api.post('/contracts/sign', data);
export const getContracts = () => api.get('/contracts');
export const getContract = (id) => api.get(`/contracts/${id}`);

// Loans
export const getLoans = () => api.get('/loans');
export const getLoan = (id) => api.get(`/loans/${id}`);

// Admin
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminFinancialReport = () => api.get('/admin/financial-report');
export const createUniversity = (data) => api.post('/admin/universities', data);
export const createProgram = (data) => api.post('/admin/programs', data);
export const updateProgram = (id, data) => api.put(`/admin/programs/${id}`, data);
export const deleteProgram = (id) => api.delete(`/admin/programs/${id}`);
export const updateUniversity = (id, data) => api.put(`/admin/universities/${id}`, data);
export const deleteUniversity = (id) => api.delete(`/admin/universities/${id}`);
export const getAllUsers = (params) => api.get('/admin/users', { params });
export const updateApplicationStatus = (id, data) => api.put(`/admin/applications/${id}/status`, data);

// Scraper
export const runScraper = (universityId) => api.post('/admin/scraper/run', null, { params: universityId ? { university_id: universityId } : {} });
export const getScrapeLogs = (limit = 10) => api.get('/admin/scraper/logs', { params: { limit } });

// Disbursements
export const getDisbursements = (universityId) => api.get('/admin/disbursements', { params: universityId ? { university_id: universityId } : {} });
export const getDisbursementSummary = () => api.get('/admin/disbursements/summary');
export const payDisbursement = (id) => api.post(`/admin/disbursements/${id}/pay`);

// Recommendations
export const getProgramRecommendations = (programId, limit = 4) => api.get(`/programs/${programId}/recommendations`, { params: { limit } });

// Scheduler & Reminders
export const runPaymentReminders = () => api.post('/admin/scheduler/run');
export const getSchedulerLogs = (limit = 20) => api.get('/admin/scheduler/logs', { params: { limit } });
export const getPaymentReminders = (limit = 50) => api.get('/admin/reminders', { params: { limit } });

// WhatsApp
export const sendWhatsAppMessage = (data) => api.post('/admin/whatsapp/send', data);
export const getWhatsAppLogs = (limit = 50) => api.get('/admin/whatsapp/logs', { params: { limit } });
export const getWhatsAppStats = () => api.get('/admin/whatsapp/stats');

// Analytics
export const getAnalytics = () => api.get('/admin/analytics');

// Referrals
export const getMyReferral = () => api.get('/referrals/my');
export const applyReferralCode = (code) => api.post('/referrals/apply', { code });
export const getAdminReferralStats = () => api.get('/admin/referrals/stats');

// Risk Score
export const getRiskScore = (userId) => api.get(`/risk-score/${userId}`);
export const calculateRiskScore = (userId) => api.post(`/risk-score/${userId}/calculate`);
export const getAdminRiskOverview = () => api.get('/admin/risk-scores/overview');

// Pre-Approval Express
export const checkPreApproval = (curp) => api.post('/pre-approval/check', { curp });
export const getPreApprovalStats = () => api.get('/admin/pre-approvals/stats');

// Onboarding (multi-country) & Documents
export const getOnboardingCountries = () => api.get('/onboarding/countries');
export const getOnboardingConfig = (country = 'MX') => api.get('/onboarding/config', { params: { country } });
export const uploadOnboardingDoc = (formData) =>
  api.post('/onboarding/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// EduPro product
export const getEduProProduct = () => api.get('/edupro/product');
export const calculateEduPro = (data) => api.post('/edupro/calculate', data);
export const createEduProApplication = (data) => api.post('/edupro/applications', data);
export const getEduProApplications = (params) => api.get('/edupro/applications', { params });
export const getEduProApplication = (id) => api.get(`/edupro/applications/${id}`);
export const verifyEduProApplication = (id) => api.post(`/edupro/applications/${id}/verify`);
export const getEduProContract = (id) => api.get(`/edupro/applications/${id}/contract`);
export const signEduProContract = (id, data) => api.post(`/edupro/applications/${id}/contract/sign`, data);
export const claimEduProApplication = (id, guestId) => api.post(`/edupro/applications/${id}/claim`, { guest_id: guestId });

// Chatbot
export const sendChatbotMessage = (message, sessionId) => 
  api.post('/chatbot/message', { message, session_id: sessionId });
export const getChatbotHistory = (limit = 20) => 
  api.get('/chatbot/history', { params: { limit } });
export const clearChatbotSession = () => api.delete('/chatbot/clear');

// Notifications
export const getNotifications = (unreadOnly = false) => 
  api.get('/notifications', { params: { unread_only: unreadOnly } });
export const markNotificationRead = (id) => api.post(`/notifications/mark-read/${id}`);
export const markAllNotificationsRead = () => api.post('/notifications/mark-all-read');

// University - Student Profiles
export const getUniversityApplicants = () => api.get('/university/applicants');
export const getStudentProfileForUniversity = (studentId) => 
  api.get(`/university/student/${studentId}`);

// Payments
export const createPaymentCheckout = (data) => api.post('/payments/create-checkout', data);
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
export const getPaymentHistory = () => api.get('/payments/history');

// Live Chat
export const getChatRooms = () => api.get('/chat/rooms');
export const getChatMessages = (roomId, limit = 50) => 
  api.get(`/chat/messages/${roomId}`, { params: { limit } });

export default api;
